import { useCallback, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { OnboardingHeader } from './components/header';
import { OnboardingIllustration } from './components/illustration';
import { ChromeExtensionInstallScreen } from './components/chrome-extension-install';
import { NewSignUpOnboarding } from './new-signup-onboarding';
import {
  Project,
  Application,
  ApplicationVersion,
  Page,
} from '@testsigmainc/models';
import { OnBoarding } from '@models/onboarding.enum';
import { TestCaseCreateRequest } from '@modules/test-development/models/test-case-create.model';
import { TestCase } from '@modules/test-development/models/test-case.model';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { HelpPreferenceContext } from '@contexts/help-preference-context';
import { useModal } from '@hooks/use-modal';
import ApplicationService from '@services/application.service';
import VersionService from '@services/version.service';
import TestCaseService from '@modules/test-development/services/test-case.service';
import useBrowserRecorder from '@hooks/use-browser-recorder';
import { InvitedUserOnboarding } from './invited-user-onboarding';
import { ApplicationType } from '@testsigmainc/constants';
import AmplitudeEventService from '@services/amplitude-event.service';
import { UseBaseHooks } from '@hooks/use-base.hooks';
import { produce } from 'immer';

export const Onboarding: React.FC = ({}) => {
  const { hasTenantPreference } = UseBaseHooks();
  const isJoinExistingAccount =
    new URLSearchParams(useLocation().search).get('isJoinExistingAccount') ||
    null;

  const userPreferenceContext = useContext(UserPreferenceContext);
  const userPreference = userPreferenceContext?.userPreference;

  const helpPreferenceContext = useContext(HelpPreferenceContext);
  const helpPreference = helpPreferenceContext?.helpPreference;

  const navigate = useNavigate();
  const browserRecorder = useBrowserRecorder();
  const chromeExtensionModal = useModal();

  // switch the context to the given project
  const switchToProject = useCallback(
    (project: Project, isCreateNew: boolean) => {
      ApplicationService.findAll(`projectId:${project.id}`).then(
        (res: Page<Application>) => {
          const application: Application = res?.content?.[0];
          VersionService.findAll(`applicationId:${application.id}`).then(
            (appRes) => {
              doChromeExtensionInstalledCheckAndHandleOnboarding(
                project,
                application,
                appRes.content[0],
                isCreateNew,
              );
            },
          );
        },
      );
    },
    [browserRecorder, chromeExtensionModal],
  );

  // join to existing account with versionID
  const switchToProjectWithVersionId = useCallback(
    (applicationVersionId: number, isCreateNew: boolean) => {
      VersionService.show(applicationVersionId).then(
        (version: ApplicationVersion) => {
          sendAmplitudeOnJoinToExistingAcccount(
            version.application.project.name,
            version.application.applicationType,
          );
          doChromeExtensionInstalledCheckAndHandleOnboarding(
            version.application.project,
            version.application,
            version,
            isCreateNew,
          );
        },
      );
    },
    [browserRecorder, chromeExtensionModal],
  );

  // do chrome extension installed check
  const doChromeExtensionInstalledCheckAndHandleOnboarding = (
    project: Project,
    application: Application,
    version: ApplicationVersion,
    isCreateNew: boolean,
  ) => {
    if (application.isMobileNative || browserRecorder.isExtensionInstalled) {
      handleOnBoarding(project, application, version, isCreateNew);
    } else {
      chromeExtensionModal.openModal({
        project,
        application,
        version,
        isCreateNew,
      });
    }
  };

  // on close modal
  const onCloseChromeExtensionModal = useCallback(() => {
    handleOnBoarding(
      chromeExtensionModal.state.data.project,
      chromeExtensionModal.state.data.application,
      chromeExtensionModal.state.data.version,
      chromeExtensionModal.state.data.isCreateNew,
    );
  }, [chromeExtensionModal]);

  // handles onboarding on create new project or choose the sample project
  const handleOnBoarding = (
    project: Project,
    application: Application,
    version: ApplicationVersion,
    isCreateNew: boolean,
  ) => {
    userPreference.preference.projectPreference = produce(
      userPreference.preference.projectPreference,
      (draft) => {
        draft.selectedVersion = version;
        draft.selectedProject = project;
        draft.selectedApplication = application;
        draft.versionId = version.id;
        draft.projectId = project.id;
        draft.applicationId = application.id;
      },
    );
    userPreferenceContext.setOrUpdatePreference(userPreference, () =>
      onUpdateUserPreference(isCreateNew, version.id),
    );
  };

  // on saving help preference remove both ON_BOARDING_INFO and ON_BOARDING_EXPLORE
  const saveHelpPreferences = useCallback(() => {
    let indexValue = helpPreference?.preferences.indexOf(
      OnBoarding.ON_BOARDING_INFO,
    );
    if (indexValue > -1) {
      helpPreference?.preferences.splice(indexValue, 1);
    }

    indexValue = helpPreference?.preferences.indexOf(
      OnBoarding.ON_BOARDING_EXPLORE,
    );
    if (indexValue > -1) {
      helpPreference?.preferences.splice(indexValue, 1);
    }

    helpPreferenceContext.setOrUpdatePreference(helpPreference, () => {});
  }, [helpPreference, helpPreferenceContext]);

  // callback after updating preference
  const onUpdateUserPreference = (isCreateNew: boolean, versionId: number) => {
    if (isCreateNew) {
      createAndNavigateToNewTestCasePage();
    } else {
      navigateToTestCaseListPage(versionId);
    }
  };

  // create new test case and navigate to steps page
  const createAndNavigateToNewTestCasePage = () => {
    const request = new TestCaseCreateRequest();
    request.applicationVersionId =
      userPreference.preference.projectPreference.versionId;
    request.isStepGroup = false;
    TestCaseService.create(request).then((testCase: TestCase) => {
      saveHelpPreferences();
      navigate(
        `/td/${userPreference.preference.projectPreference.versionId}/cases/filters/${testCase.id}`,
      );
    });
  };

  // navigate to test case list page
  const navigateToTestCaseListPage = (versionId?) => {
    saveHelpPreferences();
    navigate(`/td/${versionId}/cases`);
  };

  useEffect(() => {
    if (hasTenantPreference('onBoardingPopup')) {
      navigateToTestCaseListPage(5);
    }
  }, []);

  // send track event to Amplitude, need to be here since they want projectName and applicationType
  const sendAmplitudeOnJoinToExistingAcccount = (
    projectName: string,
    applicationType: ApplicationType,
  ) => {
    AmplitudeEventService.onJoinToExistingAccount(
      'User Invite Join Account',
      'Join existing account',
      projectName,
      applicationType,
    );
  };

  return (
    <>
      {!hasTenantPreference('onBoardingPopup') && (
        <div className='w-full'>
          {/* Header Logo */}
          <OnboardingHeader />

          <div className='flex justify-center w-full h-[calc(100%-100px)]'>
            {/* onboarding illustration logo */}
            <div className='w-[44%] flex items-center justify-center h-max'>
              <OnboardingIllustration />
            </div>

            {/* Onboarding Content based on is join to existing account queryparam */}
            {isJoinExistingAccount ? (
              <InvitedUserOnboarding
                switchToProjectWithVersionId={switchToProjectWithVersionId}
              />
            ) : (
              <NewSignUpOnboarding switchToProject={switchToProject} />
            )}
          </div>
        </div>
      )}
      {/* Chrome extension modal */}
      {chromeExtensionModal.state.show &&
        !hasTenantPreference('onBoardingPopup') && (
          <ChromeExtensionInstallScreen
            onClose={onCloseChromeExtensionModal}
            show={true}
          />
        )}
    </>
  );
};
